import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import PropTypes from "prop-types";
import React from "react";
import { selectFilter, textFilter } from "react-bootstrap-table2-filter";
import { Badge } from "reactstrap";
import {
  APPROVE_STATUS_COLOR,
  APPROVE_STATUS_DRAFT,
  APPROVE_STATUS_LABEL,
  APPROVE_STATUS_SUBMITTED,
  CQT_STATUS_DRAFT,
  IS_TEST,
  CQT_STATUS_COLOR,
  CQT_STATUS_LABEL,
  STATEMENT_PDF_STATUS_GENERATED,
  STATEMENT_TYPE_LABEL,
} from "../constants";
import { formatInvoiceDate, formatMonthLabel, formatMoney } from "../helper";

// Tên bên bán / bên mua dài, giới hạn bề rộng và cho xuống dòng, kèm title
// để hover xem đủ
const partyColumn = (dataField, text) => ({
  dataField,
  text,
  headerStyle: { width: 220 },
  formatter: cell => (
    <div style={{ maxWidth: 220 }} title={cell?.name || ""}>
      <div className="text-wrap">{cell?.name || ""}</div>
      {cell?.tax_code && (
        <div className="text-muted fw-bold font-size-13">
          MST: {cell.tax_code}
        </div>
      )}
    </div>
  ),
});

const StatementListTable = props => {
  const {
    items,
    page,
    sizePerPage,
    totalSize,
    loading,
    onPageChange,
    onView,
    onViewPdf,
    onViewReject,
    onFilterChange,
    onSubmit,
    onApprove,
    onReject,
    onCancel,
    canSubmit,
    canApprove,
    canCancel,
  } = props;

  const columns = [
    {
      dataField: "id",
      text: "ID",
      // fieldSearch để RemoteDataTable gửi id=... thay vì filter[id]=...
      fieldSearch: "id",
      headerStyle: { width: 90 },
      filter: textFilter({
        placeholder: "Nhập ID",
        style: { width: 80 },
      }),
    },
    {
      dataField: "code",
      text: "Số bảng kê",
      formatter: cell => <span className="fw-bold text-primary">{cell}</span>,
    },
    {
      dataField: "period",
      text: "Kỳ",
      formatter: cell => formatMonthLabel(cell),
    },
    {
      dataField: "cqt_status",
      text: "Trạng thái",
      // fieldSearch để RemoteDataTable gửi cqt_status=... thay vì
      // filter[cqt_status]=...
      fieldSearch: "cqt_status",
      filter: selectFilter({
        placeholder: "Tất cả",
        options: CQT_STATUS_LABEL,
        style: { width: 130 },
      }),
      // Cùng khuôn badge với cột trạng thái ở ListInvoice
      formatter: (cell, row) => (
        <div className="d-flex align-items-center gap-1">
          <Badge
            className={`font-size-12 badge-soft-${
              CQT_STATUS_COLOR[cell] || "secondary"
            }`}
            pill
          >
            {CQT_STATUS_LABEL[cell] || cell || ""}
          </Badge>
          {/* Chỉ có icon khi CQT thực sự trả về lý do từ chối */}
          {row.reject_reason && (
            <button
              type="button"
              className="btn btn-sm btn-link p-0"
              title="Hiển thị lý do từ chối"
              aria-label="Hiển thị lý do từ chối"
              onClick={() => onViewReject(row)}
            >
              <i className="bx bx-info-circle font-size-17 text-danger" />
            </button>
          )}
        </div>
      ),
    },
    {
      dataField: "approve_status",
      text: "Trạng thái duyệt",
      fieldSearch: "approve_status",
      filter: selectFilter({
        placeholder: "Tất cả",
        options: APPROVE_STATUS_LABEL,
        style: { width: 140 },
      }),
      // BE chưa trả approve_status, chưa có thì để gạch ngang cho rõ
      formatter: cell =>
        cell ? (
          <Badge
            className={`font-size-12 badge-soft-${
              APPROVE_STATUS_COLOR[cell] || "secondary"
            }`}
            pill
          >
            {APPROVE_STATUS_LABEL[cell] || cell}
          </Badge>
        ) : (
          <span className="text-muted">–</span>
        ),
    },
    {
      dataField: "adjust_mode",
      text: "Hình thức",
      formatter: cell => STATEMENT_TYPE_LABEL[cell] || cell || "",
    },
    {
      dataField: "report_date",
      text: "Ngày lập",
      formatter: cell => formatInvoiceDate(cell),
    },
    partyColumn("seller", "Bên bán"),
    partyColumn("buyer", "Bên mua"),
    {
      dataField: "invoice_count",
      text: "Số lượng HĐ",
    },
    // {
    //   dataField: "total_rows",
    //   text: "Số dòng",
    //   align: "right",
    //   headerAlign: "right",
    // },
    // {
    //   dataField: "diff_pay",
    //   text: "Chênh lệch",
    //   align: "right",
    //   headerAlign: "right",
    //   formatter: cell =>
    //     cell === null || cell === undefined ? (
    //       <span className="text-muted">–</span>
    //     ) : (
    //       formatMoney(cell)
    //     ),
    // },
  ];

  // Tự dựng cột thao tác thay vì dùng listRowAction: nút xem PDF chỉ được hiện
  // khi pdf_status đúng bằng "generated", mà conditionVisible của
  // RemoteDataTable không diễn tả được điều kiện đó khi field trả về null.
  columns.push({
    dataField: "__action",
    text: "Thao tác",
    isDummyField: true,
    align: "center",
    headerAlign: "center",
    headerStyle: { width: 110, textAlign: "center" },
    formatter: (cell, row) => (
      <div className="d-flex justify-content-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-link p-0"
          title="Xem bảng kê"
          aria-label="Xem bảng kê"
          onClick={() => onView(row)}
        >
          <i className="bx bx-show font-size-18 text-primary" />
        </button>
        {/* Cửa hàng gửi bảng kê còn lưu tạm cho kế toán duyệt */}
        {(IS_TEST ||
          (canSubmit &&
            row.cqt_status === CQT_STATUS_DRAFT &&
            (!row.approve_status ||
              row.approve_status === APPROVE_STATUS_DRAFT))) && (
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            title="Gửi duyệt"
            aria-label="Gửi duyệt"
            onClick={() => onSubmit(row)}
          >
            <i className="bx bx-send font-size-18 text-primary" />
          </button>
        )}
        {/* Kế toán duyệt / từ chối bảng kê đã gửi. Ba icon dùng đúng bộ của
            luồng duyệt bên hóa đơn: gửi duyệt, duyệt, từ chối duyệt */}
        {(IS_TEST ||
          (canApprove && row.approve_status === APPROVE_STATUS_SUBMITTED)) && (
          <React.Fragment>
            <button
              type="button"
              className="btn btn-sm btn-link p-0"
              title="Duyệt bảng kê"
              aria-label="Duyệt bảng kê"
              onClick={() => onApprove(row)}
            >
              <i className="bx bxs-check-circle font-size-18 text-success" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-link p-0"
              title="Từ chối duyệt bảng kê"
              aria-label="Từ chối duyệt bảng kê"
              onClick={() => onReject(row)}
            >
              <i className="bx bxs-x-circle font-size-18 text-danger" />
            </button>
          </React.Fragment>
        )}
        {/* Bảng kê còn lưu tạm thì được hủy bỏ */}
        {(IS_TEST ||
          (canCancel && row.approve_status === APPROVE_STATUS_DRAFT)) && (
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            title="Hủy bảng kê"
            aria-label="Hủy bảng kê"
            onClick={() => onCancel(row)}
          >
            <i className="bx bx-trash font-size-18 text-danger" />
          </button>
        )}
        {row.pdf_status === STATEMENT_PDF_STATUS_GENERATED && (
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            title="Xem PDF"
            aria-label="Xem PDF"
            onClick={() => onViewPdf(row)}
          >
            <i className="bx bxs-file-pdf font-size-18 text-danger" />
          </button>
        )}
      </div>
    ),
  });

  const handleTableChange = (type, newState) => {
    if (type === "filter") {
      // Lọc lại thì về trang 1, giữ nguyên số dòng mỗi trang
      onFilterChange(newState.columnFilter || {});
      return;
    }
    onPageChange(newState.page, newState.sizePerPage);
  };

  return (
    <RemoteDataTable
      keyField="id"
      data={items}
      columns={columns}
      page={page}
      sizePerPage={sizePerPage}
      totalSize={totalSize}
      loading={loading}
      onTableChange={handleTableChange}
      rowEvents={{ onDoubleClick: (event, row) => onView(row) }}
      // API danh sách chưa có tham số tìm kiếm toàn văn nên tắt ô này đi,
      // tránh để một ô nhập không tác dụng
      isShowSearchAll={false}
    />
  );
};

StatementListTable.propTypes = {
  items: PropTypes.array,
  page: PropTypes.number,
  sizePerPage: PropTypes.number,
  totalSize: PropTypes.number,
  loading: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onViewPdf: PropTypes.func.isRequired,
  onViewReject: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onCancel: PropTypes.func,
  canSubmit: PropTypes.bool,
  canApprove: PropTypes.bool,
  canCancel: PropTypes.bool,
};

StatementListTable.defaultProps = {
  onSubmit: undefined,
  onApprove: undefined,
  onReject: undefined,
  onCancel: undefined,
  canSubmit: false,
  canApprove: false,
  canCancel: false,
  items: [],
  page: 1,
  sizePerPage: 10,
  totalSize: 0,
  loading: false,
};

export default StatementListTable;
